import { useMemo } from 'react';

import { PlusOutlined, QuestionCircleOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Flex, Dropdown, Colors, typographyStyle } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { Table, Toast } from '@revenuemarket/ui';
import { Button, Tooltip, Typography, Modal } from 'antd';

import Content from '~components/ui/DataManageContent';
import CertificateRegisterModal from '~components/ui/Modal/CertificateRegisterModal';
import CertificateEditModal from '~components/ui/Modal/CertificateRegisterModal/CertificateEditModal';
import { useGetOrganizationsInfo } from '~hooks/queries/organizations/query';
import useCertificatesQuery from '~hooks/queries/useCertificatesQuery';
import useDeleteCertificationsMutation from '~hooks/queries/useDeleteCertificationsMutation';
import useCheckOnboardStatus from '~hooks/useCheckOnboardStatus';
import { useCompanyId } from '~hooks/useCompanyId';

import BankListRenderer from './BankListRenderer';
import CardListRenderer from './CardListRenderer';
import HomeTaxRenderer from './HomeTaxRender';
import CertificateInfo from './Info';
import SecuritiesListRenderer from './SecuritiesListRenderer';
import { ClickAnimationButton } from '../../styles';

const headers = (showCard?: boolean) => {
  return showCard
    ? [
        {
          label: '이름',
        },
        {
          label: '은행',
        },
        {
          label: '증권사',
        },
        {
          label: '카드',
        },
        {
          label: '홈택스',
        },
        {
          label: '',
        },
      ]
    : [
        {
          label: '이름',
        },
        {
          label: '은행',
        },
        {
          label: '증권사',
        },
        {
          label: '홈택스',
        },
        {
          label: '',
        },
      ];
};

interface Props {
  showCard?: boolean;
  initScrape?: boolean;
}

const CertificateComponent: React.FC<Props> = ({ showCard, initScrape = false }) => {
  const { companyId } = useCompanyId();

  const { mutateAsync: deleteCertificatesAsync } = useDeleteCertificationsMutation();
  const { certificateRegistered } = useCheckOnboardStatus();
  const { data: certificate, refetch } = useCertificatesQuery(companyId);
  const { refetch: refetchOrganizations } = useGetOrganizationsInfo();
  const certificateList = certificate?.Data?.data?.certificates || [];

  const items = useMemo(() => {
    return certificateList.map((certificate) => {
      const CertificateActions = () => {
        const handleDelete = async () => {
          const result = await deleteCertificatesAsync({
            companyId: companyId,
            certificateId: certificate.certificateId,
          });
          if (result?.Status === ResponseStatus.SUCCESS) {
            refetch();
            refetchOrganizations();
            Toast.success('공동인증서가 삭제되었습니다.');
          } else {
            Toast.error('공동인증서 삭제 중 문제가 발생했습니다.');
          }
        };

        const dropdownMenu = {
          items: [
            {
              key: 'edit',
              label: (
                <span style={{ ...typographyStyle['14_400'], color: Colors.gray800 }}>수정</span>
              ),
              onClick: () => {
                overlays.open(({ isOpen, onClose }) => (
                  <CertificateEditModal
                    isOpen={isOpen}
                    onClose={onClose}
                    certificate={certificate}
                  />
                ));
              },
            },
            {
              key: 'delete',
              label: <span style={{ ...typographyStyle['14_400'], color: '#da2333' }}>삭제</span>,
              onClick: () => {
                Modal.confirm({
                  title: '공동인증서를 삭제하시겠습니까?',
                  onOk: handleDelete,
                  okText: '네',
                  cancelText: '아니오',
                  okButtonProps: { danger: true },
                });
              },
            },
          ],
        };

        return (
          <Dropdown menu={dropdownMenu}>
            <Button
              type="text"
              icon={<EllipsisOutlined />}
              style={{
                width: 32,
                height: 32,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(29, 34, 37, 0.12)',
                borderRadius: 8,
              }}
            />
          </Dropdown>
        );
      };
      return showCard
        ? [
            {
              element: <CertificateInfo certificate={certificate} />,
            },
            {
              element: <BankListRenderer certificate={certificate} />,
            },
            {
              element: (
                <SecuritiesListRenderer
                  certificate={{
                    ...certificate,
                  }}
                />
              ),
            },
            {
              element: <CardListRenderer certificate={certificate} />,
            },
            {
              element: <HomeTaxRenderer certificate={certificate} />,
            },
            {
              element: <CertificateActions />,
            },
          ]
        : [
            {
              element: <CertificateInfo certificate={certificate} />,
            },
            {
              element: <BankListRenderer certificate={certificate} />,
            },
            {
              element: <SecuritiesListRenderer certificate={certificate} />,
            },
            {
              element: <HomeTaxRenderer certificate={certificate} />,
            },
            {
              element: <CertificateActions />,
            },
          ];
    });
  }, [
    certificateList,
    showCard,
    companyId,
    deleteCertificatesAsync,
    refetch,
    refetchOrganizations,
  ]);

  return (
    <div style={{ marginTop: 24 }}>
      <Content
        title="공동인증서"
        actionButton={
          <ClickAnimationButton
            animationDisabled={certificateRegistered}
            type="primary"
            icon={<PlusOutlined />}
            onClick={async () => {
              overlays.open(({ isOpen, onClose }) => {
                return <CertificateRegisterModal open={isOpen} onCancel={onClose} />;
              });
            }}
          >
            등록
          </ClickAnimationButton>
        }
        description={
          <Flex direction="column" gap={24}>
            <Flex gap={4}>
              <Typography.Text style={{ color: colorTokens.textDescription, fontSize: 14 }}>
                이용하시는 모든 은행과 홈택스에 로그인 가능한 공동인증서를 추가해주세요.
              </Typography.Text>
              <Tooltip
                placement="top"
                title="클로브는 은행 거래내역과 세금계산서를 중심으로 현금흐름을 분석하므로, 클로브를 정상적으로 이용하기 위해서는 은행 및 홈택스 연결이 꼭 필요합니다."
              >
                <QuestionCircleOutlined style={{ color: colorTokens.iconDefault, fontSize: 16 }} />
              </Tooltip>
            </Flex>
          </Flex>
        }
        alertInfo={
          certificateList?.length === 0
            ? {
                title: '모든 공동인증서를 추가해주세요',
                description: '은행, 홈택스에 로그인 가능한 공동인증서를 추가해주세요. ',
              }
            : null
        }
      >
        <Table
          borderColor="rgba(0, 0, 0, 0.06)"
          headers={headers(showCard)}
          title={`총 ${certificateList.length}개`}
          items={items}
          gridTemplateColumns={
            showCard ? '1fr 240px 240px 240px 80px 32px' : '1fr 240px 240px 80px 32px'
          }
          emptyDescription="등록된 공동인증서가 없습니다"
        />
      </Content>
    </div>
  );
};

export default CertificateComponent;
